<script lang="ts" setup>
import { computed, PropType } from "vue";
import { TirPmButton, TirPmButtonSizeEnum, TirPmButtonVariantEnum } from "tir-components";
import { ChevronDownIcon } from "tir-style-system/icons/solid";
import { TaskColumnModel, TaskSortingModel } from "../../models";
import { TaskViewElementEnum } from "@/components/tasks-kanban/enums";

// #region defineProps
/**Параметры */
const props = defineProps({
  /**Колонки */
  columns: { type: Array as PropType<TaskColumnModel[]> },
  /**Данные сортировки */
  sortData: { type: Object as PropType<TaskSortingModel> },
  /**Включить возможность сортировки */
  enableSorting: { type: Boolean, default: false },
  /**Настройки отображения */
  viewSettings: {
    type: Array as PropType<Array<TaskViewElementEnum>>,
    default: () => [
      TaskViewElementEnum.Namespace,
      TaskViewElementEnum.Queue,
      TaskViewElementEnum.Number,
      TaskViewElementEnum.Name,
      TaskViewElementEnum.Status,
      TaskViewElementEnum.Difficulty,
      TaskViewElementEnum.Priority,
      TaskViewElementEnum.EstimateTime,
      TaskViewElementEnum.WorkTime,
    ],
  },
  /**Загрузка при сортировке */
  isLoading: {
    type: Boolean,
    default: false,
  },
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие обновления данных сортировки */
  (e: "update:sortData", value: TaskSortingModel): void;
}>();
// #endregion defineEmits

// #region Переменные
/**Колонки по которым можно сортировать */
const sortingColumnsType = [
  TaskViewElementEnum.Number,
  TaskViewElementEnum.Name,
  TaskViewElementEnum.EstimateTime,
  TaskViewElementEnum.WorkTime,
];
// #endregion Переменные

// #region computed
/**Минимальная ширина */
const minWidth = computed(() => {
  const columnsCalcStr = props.columns.map((column) => column.MinWidth).join(" + ");

  return `calc(${columnsCalcStr} + ${0.75 * (props.columns.length - 1)}rem + 1.25rem)`;
});
/**Видимые колонки */
const visibleColumns = computed(() =>
  props.columns.filter((column) => props.viewSettings.includes(column.Type)),
);
// #endregion computed

// #region Функции
/**
 * Выбрать колонку по которой сортируются задачи
 * @param column Колонка
 */
const selectSortingColumn = (column: TaskColumnModel) => {
  let sortData = new TaskSortingModel(props.sortData);

  switch (column.Type) {
    case TaskViewElementEnum.Number:
      sortData = new TaskSortingModel({
        OrderById: !sortData.OrderById,
      });
      break;
    case TaskViewElementEnum.Name:
      sortData = new TaskSortingModel({
        OrderByName: !sortData.OrderByName,
      });
      break;
    case TaskViewElementEnum.EstimateTime:
      sortData = new TaskSortingModel({
        OrderByEstimateTime: !sortData.OrderByEstimateTime,
      });
      break;
    case TaskViewElementEnum.WorkTime:
      sortData = new TaskSortingModel({
        OrderByWorkTime: !sortData.OrderByWorkTime,
      });
      break;
  }

  emit("update:sortData", sortData);
};

const checkIsRotateChevron = (columnType: TaskViewElementEnum) => {
  if (!props.sortData) return false;

  switch (columnType) {
    case TaskViewElementEnum.Number:
      return !props.sortData.OrderById;
    case TaskViewElementEnum.Name:
      return !props.sortData.OrderByName;
    case TaskViewElementEnum.EstimateTime:
      return !props.sortData.OrderByEstimateTime;
    case TaskViewElementEnum.WorkTime:
      return !props.sortData.OrderByWorkTime;
  }
  return false;
};
// #endregion Функции
</script>
<template>
  <div>
    <div class="task-header" :style="{ minWidth }">
      <div
        :class="['task-header__item', `align--${column.Align}`]"
        v-for="column in visibleColumns"
        :style="{
          width: column.Width,
          minWidth: column.MinWidth,
        }"
        :key="column.Name"
      >
        <span class="task-header__item-name">{{ column.Name }}</span>
        <TirPmButton
          v-if="enableSorting && sortingColumnsType.includes(column.Type)"
          class="task-header__item-sort-button"
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          :is-loading="isLoading"
          @click="selectSortingColumn(column)"
        >
          <template #leftSlot>
            <ChevronDownIcon
              :class="[
                'task-header__item-sort-button-icon',
                { 'is-rotate': checkIsRotateChevron(column.Type) },
              ]"
            />
          </template>
        </TirPmButton>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.task-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 0.688rem;
  width: 100%;

  &__item {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    white-space: nowrap;
    flex-shrink: 0;

    &.align--left {
      justify-content: flex-start;
    }
    &.align--center {
      justify-content: center;
    }
    &.align--right {
      justify-content: flex-end;
    }
  }
  &__item-name {
    color: var(--text-tertiary, #93969b);
    text-align: right;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
  }
  &__item-sort-button-icon {
    transition: transform 0.2s ease-in-out;

    &.is-rotate {
      transform: rotate(180deg);
    }
  }
}
</style>
